<script setup lang="ts">
import { computed, ref } from "vue";

import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

import { useBrandStore, useMessageStore, useRetentionStore } from "@/stores";

import { isApiError } from "@/services/Api";
import {
  addDuration,
  getDateISOString,
  parseDuration,
  toISODuration,
  dateInputToUTCDateTime,
  DURATION_PARTS_ORDER,
} from "@/services/UtilitiesService";

import { assertRetentionDurationWithinBounds } from "./retention-duration-validation";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

withDefaults(defineProps<{
  durationState: "UNSET" | "CASE-INDEFINITE" | "CASE" | "BRAND";
  currentDurationISO: string | null | undefined;
  currentExpiry: Date | undefined;
  startDate: Date;
}>(), {  })

const brandStore = useBrandStore();
const caseRouteParams = useCaseRouteParams();

const retentionBrandConfig = computed(() => brandStore.brandConfig.retention);
const retentionUIConfig = computed(() => brandStore.viewerConfig.retention ?? {});

const enableDateCalendar = computed(() => retentionUIConfig.value.enableDateCalendar === true);
const enableDurationEntry = computed(() => retentionUIConfig.value.enableMDHEntry === true);
const enableIndefinite = computed(
  () => retentionBrandConfig.value.casesMayBeSelectedForIndefiniteRetention && __props.durationState !== "CASE-INDEFINITE",
);
const requireReason = computed(() => retentionBrandConfig.value.retentionDurationChangesRequireReason);

const selectedMethod = ref(
  enableDateCalendar.value
    ? ("date" as const)
    : enableDurationEntry.value
      ? ("duration" as const)
      : enableIndefinite.value
        ? ("indefinite" as const)
        : undefined,
);

const currentDuration = computed(() => (__props.currentDurationISO == null ? {} : parseDuration(__props.currentDurationISO)));
const expiryDurationPartsValue = ref(currentDuration.value);

const expiryDateValue = ref(getDateISOString(__props.currentExpiry));

const adjustedISODuration = computed(() => toISODuration(expiryDurationPartsValue.value));
const adjustedExpiryDate = computed(() => {
  if (expiryDateValue.value === "") return null;
  return dateInputToUTCDateTime(expiryDateValue.value, true);
});
const adjustedDate = computed(() =>
  selectedMethod.value === "duration"
    ? addDuration(__props.startDate, adjustedISODuration.value ?? "P0D")
    : selectedMethod.value === "date"
      ? adjustedExpiryDate.value
      : null,
);

const reason = ref("");
const caseRefInput = ref("");

const state = ref<
  { status: "idle" } | { status: "loading"; action: "clear" | "set" } | { status: "error"; message: string }
>({ status: "idle" });

const isSubmitDisabled = computed(
  () => state.value.status === "loading" || caseRefInput.value !== caseRouteParams.value.casename,
);

function handleSubmit(e: SubmitEvent) {
  if (isSubmitDisabled.value || !(e.submitter instanceof HTMLButtonElement)) return;

  e.preventDefault();

  if (e.submitter.id === CLEAR_BTN_ID) clearCaseLevelRetentionDuration();
  else if (e.submitter.id === SET_BTN_ID) setCaseLevelRetentionDuration();
}

async function clearCaseLevelRetentionDuration() {
  try {
    state.value = { status: "loading", action: "clear" };

    await useRetentionStore().updateDuration({
      duration: null,
      durationState: "DurationRemoved",
      isIndefinite: false,
      reason: reason.value,
    });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Cleared case-level retention duration successfully.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to clear case-level retention duration. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}

async function setCaseLevelRetentionDuration() {
  try {
    if (selectedMethod.value === "date") {
      if (adjustedExpiryDate.value == null) throw new Error("The new expiry date is required");
      if (isNaN(adjustedExpiryDate.value.getTime())) throw new Error("The new expiry date is invalid");
      if (adjustedExpiryDate.value.getTime() === (__props.currentExpiry?.getTime() ?? 0))
        throw new Error("The new expiry date is unchanged.");
    } else if (selectedMethod.value === "duration") {
      if (adjustedISODuration.value == null) throw new Error("The new retention duration is required");
      if (adjustedISODuration.value === __props.currentDurationISO) throw new Error("The new retention duration is unchanged.");
    }

    if (selectedMethod.value !== "indefinite") {
      if (adjustedDate.value == null) throw new Error("Error calculating adjusted date for retention duration.");

      assertRetentionDurationWithinBounds(adjustedDate.value, {
        startDate: __props.startDate,
        currentDuration: __props.currentDurationISO,
        maxExtensionDuration: retentionBrandConfig.value.maxExtensionDuration,
        minExtensionDuration: retentionBrandConfig.value.defaultRetentionDuration,
        canReduce: retentionBrandConfig.value.authorizedUsersMayReduceRetentionDuration,
        canReduceBelowMin: retentionBrandConfig.value.authorizedUsersMayReduceBelowBrandMinimum,
      });
    }

    state.value = { status: "loading", action: "set" };

    await useRetentionStore().updateDuration({
      isIndefinite: selectedMethod.value === "indefinite",
      reason: reason.value,
      duration:
        selectedMethod.value === "date"
          ? adjustedExpiryDate.value!.toISOString()
          : selectedMethod.value === "duration"
            ? adjustedISODuration.value
            : null,
      durationState:
        selectedMethod.value === "date"
          ? "DurationSetViaDate"
          : selectedMethod.value === "duration"
            ? "DurationSetViaDuration"
            : "DurationUnchanged",
    });

    state.value = { status: "idle" };

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Retention duration updated successfully.",
    });
  } catch (error) {
    if (!(error instanceof Error)) console.error(error);

    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : error instanceof Error
          ? error.message
          : "Failed to Update Retention Duration.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}

const SET_BTN_ID = "set-case-level-duration";
const CLEAR_BTN_ID = "clear-case-level-duration";

const DURATION_METHOD_DATE = "edit-retention-duration-method-date";
const DURATION_METHOD_DURATION = "edit-retention-duration-method-duration";
const DURATION_METHOD_INDEFINITE = "edit-retention-duration-method-indefinite";
</script>
<template>
  <details v-if="enableDateCalendar || enableDurationEntry || enableIndefinite">
    <summary>
      {{ durationState === "UNSET" ? "Set Retention Duration" : "Edit Retention Duration" }}
    </summary>
    <form class="tw-flex tw-flex-col tw-gap-3 tw-pl-4" @submit.prevent="handleSubmit">
      <p
        v-if="durationState === 'UNSET' || durationState === 'CASE-INDEFINITE'"
        class="tw-flex tw-items-center tw-gap-1 tw-text-sm"
      >
        <i class="fas fa-circle-exclamation tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
        {{
          durationState === "UNSET"
            ? "No retention duration has been set for this case."
            : "This case is currently retained indefinitely."
        }}
      </p>
      <fieldset class="tw-flex tw-flex-col tw-gap-1.5">
        <legend class="tw-mb-1 tw-text-xs">Select method</legend>
        <div v-if="enableDateCalendar" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
          <input :id="DURATION_METHOD_DATE" v-model="selectedMethod" name="method" type="radio" value="date" required />
          <label :for="DURATION_METHOD_DATE">New Case Expiry Date</label>
        </div>
        <div v-if="enableDurationEntry" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
          <input
            :id="DURATION_METHOD_DURATION"
            v-model="selectedMethod"
            name="method"
            type="radio"
            value="duration"
            required
          />
          <label :for="DURATION_METHOD_DURATION">New Case Retention Duration</label>
        </div>
        <div v-if="enableIndefinite" class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
          <input
            :id="DURATION_METHOD_INDEFINITE"
            v-model="selectedMethod"
            name="method"
            type="radio"
            value="indefinite"
            required
          />
          <label :for="DURATION_METHOD_INDEFINITE">Retain Case Indefinitely</label>
        </div>
      </fieldset>
      <div v-if="selectedMethod === 'date'" class="tw-flex tw-flex-col tw-gap-1">
        <label :for="`${DURATION_METHOD_DATE}-value`" class="tw-text-xs">New Case Expiry Date</label>
        <input
          :id="`${DURATION_METHOD_DATE}-value`"
          v-model="expiryDateValue"
          type="date"
          class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
          :min="getDateISOString(new Date())"
        />
      </div>
      <fieldset v-else-if="selectedMethod === 'duration'" class="tw-flex tw-flex-col tw-gap-1">
        <legend class="tw-mb-1 tw-text-xs">New Case Retention Duration</legend>
        <div class="tw-flex tw-flex-wrap tw-gap-2">
          <div v-for="part in DURATION_PARTS_ORDER" :key="part.prop" class="tw-flex tw-flex-1 tw-flex-col tw-gap-0.5">
            <label :for="`${DURATION_METHOD_DURATION}-${part.prop}`" class="tw-text-xs tw-capitalize">{{
              part.prop + "s"
            }}</label>
            <input
              :id="`${DURATION_METHOD_DURATION}-${part.prop}`"
              v-model.number="expiryDurationPartsValue[part.prop]"
              type="number"
              min="0"
              class="tw-w-full tw-rounded tw-border tw-p-2 tw-text-sm"
              placeholder="0"
              :aria-label="part.prop + 's'"
            />
          </div>
        </div>
        <p v-if="adjustedDate != null">
          Adjusted Date: {{ adjustedDate.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }) }}
        </p>
      </fieldset>
      <p v-else-if="selectedMethod === 'indefinite'" class="tw-text-sm tw-text-gray-600">
        The case will be retained indefinitely and will not be subject to automatic deletion.
      </p>
      <RetentionActionConfirmCase
        v-model:reason="reason"
        v-model:case-ref="caseRefInput"
        :require-reason="requireReason"
      />
      <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ state.message }}
      </p>
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1 *:tw-shrink-0">
        <button
          v-if="durationState === 'CASE' || durationState === 'CASE-INDEFINITE'"
          :id="CLEAR_BTN_ID"
          type="submit"
          class="tw-btn-md-red tw-b-r tw-flex tw-items-center tw-gap-1 *:tw-min-w-0"
          :disabled="isSubmitDisabled"
        >
          <i
            v-if="state.status === 'loading' && state.action === 'clear'"
            aria-hidden="true"
            class="fas fa-spin fa-circle-notch"
          />
          {{ durationState === "CASE-INDEFINITE" ? "Clear Indefinite Retention" : "Clear Case-Level Duration" }}
        </button>
        <div class="tw-flex-auto" />
        <button
          :id="SET_BTN_ID"
          type="submit"
          class="tw-btn-md-gray tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-self-start"
          :disabled="isSubmitDisabled"
        >
          <i
            v-if="state.status === 'loading' && state.action === 'set'"
            aria-hidden="true"
            class="fas fa-spin fa-circle-notch"
          />
          {{
            selectedMethod === "indefinite"
              ? "Retain Indefinitely"
              : durationState === "UNSET"
                ? "Set Retention"
                : "Update Retention"
          }}
        </button>
      </div>
    </form>
  </details>
</template>
